export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'info'

export const SEVERITY_COLORS = {
  critical: {
    primary:         '#dc2626',
    background:      '#fee2e2',
    text:            '#dc2626',
    darkBackground:  'rgba(220, 38, 38, 0.15)',
    darkText:        '#f87171',
  },
  high: {
    primary:         '#f97316',
    background:      '#ffedd5',
    text:            '#f97316',
    darkBackground:  'rgba(249, 115, 22, 0.15)',
    darkText:        '#fb923c',
  },
  medium: {
    primary:         '#f59e0b',
    background:      '#fef3c7',
    text:            '#f59e0b',
    darkBackground:  'rgba(245, 158, 11, 0.15)',
    darkText:        '#fbbf24',
  },
  low: {
    primary:         '#3b82f6',
    background:      '#dbeafe',
    text:            '#3b82f6',
    darkBackground:  'rgba(59, 130, 246, 0.15)',
    darkText:        '#60a5fa',
  },
  info: {
    primary:         '#6b7280',
    background:      '#f3f4f6',
    text:            '#6b7280',
    darkBackground:  'rgba(107, 114, 128, 0.15)',
    darkText:        '#9ca3af',
  },
} as const

/** Ordered Critical → High → Medium → Low → Info (for ApexCharts color arrays) */
export const SEVERITY_CHART_COLORS: string[] = [
  SEVERITY_COLORS.critical.primary,
  SEVERITY_COLORS.high.primary,
  SEVERITY_COLORS.medium.primary,
  SEVERITY_COLORS.low.primary,
  SEVERITY_COLORS.info.primary,
]

/** Reversed order Info → Low → Medium → High → Critical (for legacy ChartsWidget8) */
export const SEVERITY_CHART_COLORS_REVERSED: string[] = [
  SEVERITY_COLORS.info.primary,
  SEVERITY_COLORS.low.primary,
  SEVERITY_COLORS.medium.primary,
  SEVERITY_COLORS.high.primary,
  SEVERITY_COLORS.critical.primary,
]

/**
 * Returns inline React style object for a severity level.
 * Pass isDark=true when data-bs-theme="dark" is active.
 */
export function getSeverityStyle(
  severity: string,
  isDark = false
): { backgroundColor: string; color: string; borderColor: string } {
  const c = SEVERITY_COLORS[(severity?.toLowerCase() as SeverityLevel)] ?? SEVERITY_COLORS.info
  return {
    backgroundColor: isDark ? c.darkBackground : c.background,
    color:           isDark ? c.darkText        : c.text,
    borderColor:     c.primary,
  }
}

/**
 * Returns a Bootstrap badge class for the severity level.
 * These classes adapt automatically to light/dark mode.
 */
export function getSeverityBadgeClass(severity: string): string {
  switch (severity?.toLowerCase()) {
    case 'critical': return 'badge-light-icritical'
    case 'high':     return 'badge-light-ihigh'
    case 'medium':   return 'badge-light-imedium'
    case 'low':      return 'badge-light-ilow'
    case 'info':     return 'badge-light-iinfo'
    default:         return 'badge-light-iinfo'
  }
}

/**
 * Maps a CVSS base score (0–10) to a SeverityLevel using the standard CVSS v3
 * qualitative bands (9+ critical, 7–8.9 high, 4–6.9 medium, 0.1–3.9 low, 0 info).
 * Lets CVSS2/CVSS3 scores reuse the app's Severity color palette so their badges
 * match the Severity indicators.
 */
export function cvssScoreToSeverityLevel(score: number): SeverityLevel {
  if (score >= 9) return 'critical'
  if (score >= 7) return 'high'
  if (score >= 4) return 'medium'
  if (score > 0) return 'low'
  return 'info'
}

/**
 * Converts numeric severity (1–5) to a SeverityLevel string.
 * Used in ScanDiff where the API returns numbers.
 */
export function numericToSeverityLevel(n: number): SeverityLevel {
  switch (n) {
    case 5: return 'critical'
    case 4: return 'high'
    case 3: return 'medium'
    case 2: return 'low'
    default: return 'info'
  }
}
